import { z } from 'zod';
import {
  IMPORT_MODES,
  MEDIA_KINDS,
  NAS_MODES,
  PREFERRED_LANGUAGE_OPTIONS,
  QUALITY_KEYS,
  ROLES,
} from './constants.js';

// ── Auth ─────────────────────────────────────────────────────────────────────
export const setupRequestSchema = z.object({
  username: z.string().min(1).max(64),
  password: z.string().min(8).max(256),
});
export type SetupRequest = z.infer<typeof setupRequestSchema>;

export const loginRequestSchema = z.object({
  username: z.string().min(1).max(64),
  password: z.string().min(1).max(256),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

// ── User management (admin only) ──────────────────────────────────────────────
export const roleSchema = z.enum(ROLES);

export const createUserRequestSchema = z.object({
  username: z.string().min(1).max(64),
  password: z.string().min(8).max(256),
  role: roleSchema,
});
export type CreateUserRequest = z.infer<typeof createUserRequestSchema>;

export const updateUserRequestSchema = z
  .object({
    role: roleSchema.optional(),
    password: z.string().min(8).max(256).optional(),
  })
  .refine((v) => v.role !== undefined || v.password !== undefined, {
    message: 'Provide a role and/or a new password',
  });
export type UpdateUserRequest = z.infer<typeof updateUserRequestSchema>;

// ── Settings ─────────────────────────────────────────────────────────────────
// Configurable base URLs must be http(s) — not file:/gopher:/etc. (a small SSRF hardening).
const httpUrl = z
  .string()
  .url()
  .refine((v) => /^https?:\/\//i.test(v), { message: 'must be an http(s) URL' });

const importModeSchema = z.enum(IMPORT_MODES);

// PATCH body. Every field optional; secrets left out (or empty) are unchanged.
export const settingsUpdateSchema = z
  .object({
    // indexer (scenenzbs / newznab)
    indexerEnabled: z.boolean(),
    indexerBaseUrl: httpUrl,
    indexerApiKey: z.string().max(512),
    indexerCategories: z.string().regex(/^\d+(,\d+)*$/, 'comma-separated category ids'),
    indexerHourlyApiCap: z.number().int().min(1).max(100_000),
    indexerMinIntervalMs: z.number().int().min(250).max(600_000),
    // downloader (NZBGet)
    nzbgetUrl: httpUrl,
    nzbgetUser: z.string().max(256),
    nzbgetPass: z.string().max(512),
    nzbgetCategory: z.string().max(128),
    downloadsPath: z.string().max(1024),
    nzbgetPathMapFrom: z.string().max(1024),
    nzbgetPathMapTo: z.string().max(1024),
    // output (Jellyfin library)
    outputMoviesPath: z.string().max(1024),
    outputShowsPath: z.string().max(1024),
    importMode: importModeSchema,
    importDryRun: z.boolean(),
    // NAS destination
    nasPath: z.string().max(1024),
    nasMode: z.enum(NAS_MODES),
    nasAutoSync: z.boolean(),
    // metadata (TMDB)
    tmdbApiKey: z.string().max(512),
    metadataUserAgentContact: z.string().max(256),
    // content filter
    preferredLanguage: z.enum(PREFERRED_LANGUAGE_OPTIONS),
    allowEnglishFallback: z.boolean(),
    maxSizeGb: z.number().int().min(0).max(1000),
    // automation
    searchEnabled: z.boolean(),
    maxConcurrentDownloads: z.number().int().min(1).max(20),
    seasonPackBeforeEpisodes: z.boolean(),
    metadataRefreshHours: z.number().int().min(1).max(720),
    removeCompletedDownloads: z.boolean(),
  })
  .partial()
  .strict();
export type SettingsUpdate = z.infer<typeof settingsUpdateSchema>;

// ── TMDB search + add to library ─────────────────────────────────────────────
export const tmdbSearchSchema = z.object({
  q: z.string().min(1).max(256),
  kind: z.enum(MEDIA_KINDS),
});

export const addMovieSchema = z.object({
  tmdbId: z.number().int().positive(),
  monitored: z.boolean().default(true),
  qualityProfileId: z.string().min(1).max(64).optional(),
  searchNow: z.boolean().default(false),
});
export type AddMovie = z.infer<typeof addMovieSchema>;

export const addSeriesSchema = z.object({
  tmdbId: z.number().int().positive(),
  monitored: z.boolean().default(true),
  qualityProfileId: z.string().min(1).max(64).optional(),
  // which episodes to monitor on add
  monitorEpisodes: z.enum(['all', 'future', 'none']).default('all'),
});
export type AddSeries = z.infer<typeof addSeriesSchema>;

const monitoredPatch = z.object({ monitored: z.boolean() }).partial().strict();
export const patchMovieSchema = z
  .object({ monitored: z.boolean(), qualityProfileId: z.string().min(1).max(64), uhd: z.boolean() })
  .partial()
  .strict();
export const patchSeriesSchema = z
  .object({ monitored: z.boolean(), qualityProfileId: z.string().min(1).max(64), uhd: z.boolean() })
  .partial()
  .strict();
export const patchSeasonSchema = monitoredPatch;
export const patchEpisodeSchema = monitoredPatch;

export const grabReleaseSchema = z.object({
  releaseId: z.string().min(1).max(64),
});

// ── Quality profiles ─────────────────────────────────────────────────────────
export const qualityKeySchema = z.enum(QUALITY_KEYS);

export const qualityItemSchema = z.object({
  quality: qualityKeySchema,
  allowed: z.boolean(),
});

export const qualityProfileWriteSchema = z.object({
  name: z.string().min(1).max(128),
  cutoff: qualityKeySchema,
  items: z.array(qualityItemSchema).min(1),
  upgradeAllowed: z.boolean().default(true),
  minSizeMb: z.number().int().min(0).max(1_000_000).default(0),
  maxSizeMb: z.number().int().min(0).max(1_000_000).default(0),
});
export type QualityProfileWrite = z.infer<typeof qualityProfileWriteSchema>;

export const testTargetSchema = z.object({
  target: z.enum(['indexer', 'downloader', 'metadata']),
});

// Free-text indexer search ("search titles, grab only those").
export const manualSearchSchema = z.object({ q: z.string().min(1).max(256) });
export const manualGrabSchema = z.object({ id: z.string().min(1).max(64) });
